// Editor de un articulo de Conocimiento: Markdown a la izquierda y vista previa en vivo a la derecha (en el celular,
// pestanas Escribir / Vista previa). Barra de botones (titulo, lista, paso, codigo, Ojo, Consejo, enlace, imagen) y
// atajos Ctrl+B / Ctrl+K. Usa la barra de cambios sin guardar de la Consola. Borrar: solo administradores y a la
// papelera de Twenty (borrado suave).
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { type Articulo, borrarArticulo, guardarArticulo } from '../../api/conocimiento';
import { useSession } from '../../auth/session';
import type { CampoCambio } from '../../lib/cambiosPendientes';
import { partesDe, rutasExistentes, SEP } from '../../lib/conocimiento';
import { ago } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { useCambiosPendientes } from '../../lib/useCambiosPendientes';
import { BarraCambios } from '../../ui/BarraCambios';
import { Markdown } from '../../ui/Markdown';
import { toast } from '../../ui/toasts';

type Datos = { titulo: string; categoria: string; resumen: string; etiquetas: string; cuerpo: string };
const CAMPOS: CampoCambio<Datos>[] = [
  { id: 'titulo', t: 'Título' },
  { id: 'categoria', t: 'Categoría' },
  { id: 'resumen', t: 'Resumen' },
  { id: 'etiquetas', t: 'Etiquetas' },
  { id: 'cuerpo', t: 'Contenido', fmt: (v) => (String(v).length > 60 ? `${String(v).slice(0, 60)}…` : String(v)) },
];

const etiquetasDe = (t: string) => [...new Set(t.split(',').map((x) => x.trim()).filter(Boolean))].slice(0, 20);

// Una accion de la barra: envuelve la seleccion o inserta un bloque en una linea nueva.
type Accion = { id: string; icono: IconName; t: string; atajo?: string; antes: string; despues?: string; bloque?: boolean; ejemplo: string };
const ACCIONES: Accion[] = [
  { id: 'titulo', icono: 'heading', t: 'Título', antes: '## ', bloque: true, ejemplo: 'Título de la sección' },
  { id: 'negrita', icono: 'bold', t: 'Negrita', atajo: 'Ctrl+B', antes: '**', despues: '**', ejemplo: 'texto' },
  { id: 'lista', icono: 'list', t: 'Lista', antes: '- ', bloque: true, ejemplo: 'Elemento' },
  { id: 'paso', icono: 'listNum', t: 'Paso', antes: '1. **', despues: '**\n   Qué hacer en este paso.', bloque: true, ejemplo: 'Nombre del paso' },
  { id: 'codigo', icono: 'code', t: 'Código', antes: '```\n', despues: '\n```', bloque: true, ejemplo: 'comando' },
  { id: 'ojo', icono: 'alert', t: 'Ojo', antes: '> **Ojo:** ', bloque: true, ejemplo: 'lo que puede salir mal' },
  { id: 'consejo', icono: 'spark', t: 'Consejo', antes: '> **Consejo:** ', bloque: true, ejemplo: 'un truco útil' },
  { id: 'enlace', icono: 'link', t: 'Enlace', atajo: 'Ctrl+K', antes: '[', despues: '](https://)', ejemplo: 'texto del enlace' },
  { id: 'imagen', icono: 'image', t: 'Imagen', antes: '![', despues: '](https://)', ejemplo: 'descripción' },
];

export function EditorArticulo({ articulo, lista, categoriaInicial, onCerrar }: { articulo: Articulo | null; lista: Articulo[]; categoriaInicial: string; onCerrar: (guardado?: string | null) => void }) {
  const qc = useQueryClient();
  const { puede } = useSession();
  const inicial = useMemo<Datos>(
    () => ({
      titulo: articulo?.titulo ?? '',
      categoria: articulo?.categoria ?? categoriaInicial,
      resumen: articulo?.resumen ?? '',
      etiquetas: (articulo?.etiquetas ?? []).join(', '),
      cuerpo: articulo?.cuerpo ?? '',
    }),
    [articulo, categoriaInicial],
  );
  const [guardadoBase, setGuardadoBase] = useState(inicial);
  const [v, setV] = useState<Datos>(inicial);
  const [guardando, setGuardando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [pestana, setPestana] = useState<'escribir' | 'ver'>('escribir');
  const area = useRef<HTMLTextAreaElement>(null);
  const rutas = useMemo(() => rutasExistentes(lista), [lista]);
  const invalido = !v.titulo.trim() ? 'escribe el título' : null;

  const guardar = async () => {
    if (invalido) {
      toast('Escribe el título del artículo.', 'error');
      return false;
    }
    setGuardando(true);
    try {
      const categoria = partesDe(v.categoria).join(SEP);
      const id = await guardarArticulo(articulo?.id ?? null, { titulo: v.titulo, categoria, resumen: v.resumen, etiquetas: etiquetasDe(v.etiquetas), cuerpo: v.cuerpo });
      setGuardadoBase(v);
      await qc.invalidateQueries({ queryKey: ['articulos'] });
      void qc.invalidateQueries({ queryKey: ['kb-buscar'] });
      toast('Artículo guardado.');
      onCerrar(id);
      return true;
    } catch (e) {
      toast((e as Error).message, 'error');
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const c = useCambiosPendientes<Datos>({
    titulo: articulo ? `Artículo «${articulo.titulo}»` : 'Artículo nuevo',
    inicial: guardadoBase,
    actual: v,
    campos: CAMPOS,
    poner: (x) => setV((p) => ({ ...p, ...x })),
    guardar,
    invalido,
  });

  const borrar = async () => {
    if (!articulo) return;
    if (!confirmar) return setConfirmar(true);
    try {
      await borrarArticulo(articulo.id);
      setGuardadoBase(v);
      void qc.invalidateQueries({ queryKey: ['articulos'] });
      toast('Artículo enviado a la papelera.');
      onCerrar(null);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  // Aplica una accion de la barra sobre la seleccion del texto.
  const aplicar = (a: Accion) => {
    const el = area.current;
    if (!el) return;
    const { selectionStart: i, selectionEnd: f, value } = el;
    const sel = value.slice(i, f) || a.ejemplo;
    let antes = value.slice(0, i);
    const despues = value.slice(f);
    if (a.bloque && antes && !antes.endsWith('\n')) antes += '\n';
    if (a.bloque && antes && !antes.endsWith('\n\n') && (a.id === 'codigo' || a.id === 'ojo' || a.id === 'consejo')) antes += '\n';
    // Una lista o pasos sobre varias lineas: cada linea con su marca.
    const cuerpo =
      a.bloque && (a.id === 'lista' || a.id === 'titulo') && sel.includes('\n')
        ? sel
            .split('\n')
            .map((l) => (l.trim() ? a.antes + l : l))
            .join('\n')
        : a.antes + sel + (a.despues ?? '');
    const nuevo = antes + cuerpo + despues;
    setV((p) => ({ ...p, cuerpo: nuevo }));
    const ini = antes.length + a.antes.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(ini, ini + sel.length);
    });
  };

  const atajos = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const k = e.key.toLowerCase();
    const a = k === 'b' ? ACCIONES.find((x) => x.id === 'negrita') : k === 'k' ? ACCIONES.find((x) => x.id === 'enlace') : null;
    if (a) {
      e.preventDefault();
      e.stopPropagation();
      aplicar(a);
    }
  };

  return (
    <div className="kbm-ed">
      <header className="kbm-ed-h">
        <button className="btn sm ghost" onClick={() => onCerrar()} aria-label="Volver al artículo">
          <Icon name="chevLeft" /> Volver
        </button>
        <div className="kbm-ed-ht">
          <h1>{articulo ? 'Editar artículo' : 'Nuevo artículo'}</h1>
          {articulo?.actualizado ? <span className="kbm-sub">{articulo.actualizadoPor ? `Actualizado por ${articulo.actualizadoPor} · ${ago(articulo.actualizado)}` : `Actualizado ${ago(articulo.actualizado)}`}</span> : null}
        </div>
        {articulo && puede('conocimiento.borrar') ? (
          <button type="button" className={`btn sm ${confirmar ? 'bad' : 'ghost'}`} onClick={() => void borrar()} onBlur={() => setConfirmar(false)}>
            <Icon name="trash" /> {confirmar ? 'Sí, enviar a la papelera' : 'Borrar'}
          </button>
        ) : null}
        <button className="btn primary" disabled={guardando || !!invalido} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </header>
      <div className="kbm-ed-campos">
        <label className={c.clase('titulo', 'kbm-ed-f kbm-ed-titulo')}>
          <span>Título</span>
          <input id="kb-titulo" value={v.titulo} onChange={(e) => setV({ ...v, titulo: e.target.value })} placeholder="Configurar TLS en un Patton SN4141" required autoFocus={!articulo} />
        </label>
        <label className={c.clase('categoria', 'kbm-ed-f')}>
          <span>Categoría</span>
          <input id="kb-cat" list="kb-rutas" value={v.categoria} onChange={(e) => setV({ ...v, categoria: e.target.value })} placeholder="Gateways Patton / Instalación" autoComplete="off" />
          <datalist id="kb-rutas">
            {rutas.map((r) => (
              <option key={r} value={r} />
            ))}
          </datalist>
        </label>
        <label className={c.clase('etiquetas', 'kbm-ed-f')}>
          <span>Etiquetas (separadas por coma)</span>
          <input id="kb-etq" value={v.etiquetas} onChange={(e) => setV({ ...v, etiquetas: e.target.value })} placeholder="Patton, TLS" autoComplete="off" />
        </label>
        <label className={c.clase('resumen', 'kbm-ed-f kbm-ed-resumen')}>
          <span>Resumen (una o dos frases: se muestra arriba y se inserta en las respuestas)</span>
          <input id="kb-resumen" value={v.resumen} onChange={(e) => setV({ ...v, resumen: e.target.value })} placeholder="Para clientes que exigen la troncal cifrada entre el gateway y su 3CX." maxLength={400} />
        </label>
      </div>
      <div className="kbm-ed-tabs" role="tablist" aria-label="Editor">
        <button type="button" role="tab" aria-selected={pestana === 'escribir'} onClick={() => setPestana('escribir')}>
          Escribir
        </button>
        <button type="button" role="tab" aria-selected={pestana === 'ver'} onClick={() => setPestana('ver')}>
          Vista previa
        </button>
      </div>
      <div className={`kbm-ed-cuerpo ver-${pestana}`}>
        <section className={c.clase('cuerpo', 'kbm-ed-md')} aria-label="Markdown">
          <div className="kbm-ed-barra" role="toolbar" aria-label="Formato">
            {ACCIONES.map((a) => (
              <button key={a.id} type="button" className="btn sm ghost" onMouseDown={(e) => e.preventDefault()} onClick={() => aplicar(a)} title={a.atajo ? `${a.t} (${a.atajo})` : a.t}>
                <Icon name={a.icono} /> <span className="lbl-m">{a.t}</span>
              </button>
            ))}
          </div>
          <textarea
            ref={area}
            id="kb-cuerpo"
            value={v.cuerpo}
            onChange={(e) => setV({ ...v, cuerpo: e.target.value })}
            onKeyDown={atajos}
            spellCheck
            aria-label="Contenido en Markdown"
            placeholder={'Escribe el artículo. Por ejemplo:\n\n1. **Entra a la web del gateway**\n   Usa el acceso guardado del cliente.\n\n> **Ojo:** abre el puerto 5061 en ambos lados.'}
          />
        </section>
        <section className="kbm-ed-prev" aria-label="Vista previa">
          <span className="kbm-eyebrow">Vista previa</span>
          <h2 className="kbm-titulo">{v.titulo || 'Sin título'}</h2>
          {v.resumen ? <p className="kbm-lead">{v.resumen}</p> : null}
          {etiquetasDe(v.etiquetas).length ? (
            <div className="kbm-chips">
              {etiquetasDe(v.etiquetas).map((e) => (
                <span key={e} className="kbm-chip tono-acc">
                  {e}
                </span>
              ))}
            </div>
          ) : null}
          {v.cuerpo.trim() ? <Markdown texto={v.cuerpo} /> : <p className="kbm-sub">Aquí verás el artículo como lo leerá el equipo.</p>}
        </section>
      </div>
      <BarraCambios />
    </div>
  );
}
